import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import apiService from "../../../../../../../../service/Api";
import { Proposal, ProposalApiResponse } from "../../../../../types/proposal.types";

export function useProposals() {
  const { id } = useParams<{ id: string }>();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [prevUrl, setPrevUrl] = useState<string | null>(null);

  const mapProposals = (results: ProposalApiResponse[]) =>
    results.map((p) => {
      const servicesTotal = p.services?.reduce((sum, s) => sum + Number(s.price), 0) ?? 0;
      const packagePrice = Number(p.package?.base_price ?? 0);
      const total = packagePrice + servicesTotal;

      return {
        id: p.id,
        title: `${p.booking?.title ?? ""} Proposal #${p.id}`.trim(),
        packageName: p.package?.name || "N/A",
        estimateTotal: `$${total.toLocaleString()}`,
        lastActivity: new Date(p.updated_at).toLocaleDateString(),
        status: (p.status.charAt(0).toUpperCase() +
          p.status.slice(1).toLowerCase()) as Proposal["status"],
        booking: p.booking,
        package: p.package,
        services: p.services,
        eventTitle: p.booking?.events?.[0]?.title ?? "",
        introduction_letter: p.introduction_letter,
        additional_notes: p.additional_notes,
        terms_and_conditions: p.terms_and_conditions,
        show_previous_booking_links: p.show_previous_booking_links,
        links: p.links,
        created_at: p.created_at,
        updated_at: p.updated_at,
        created_by: p.created_by,
        last_updated_by: p.last_updated_by,
        booking_id: p.booking?.id,
        template: p.template,
      };
    });

  const fetchProposals = useCallback(async (url?: string) => {
    if (!id) return;
    setLoading(true);
    try {
      const fetchUrl = url ?? `/api/bookings/booking-proposal/?booking=${id}`;
      const res = await apiService.get<{
        results: ProposalApiResponse[];
        count: number;
        next: string | null;
        previous: string | null;
      }>(fetchUrl);

      const stripBaseUrl = (url: string | null) => {
        if (!url) return null;
        try {
          return new URL(url).pathname + new URL(url).search;
        } catch {
          return url;
        }
      };

      setTotalCount(res.data.count);
      // In fetchProposals:
      setNextUrl(stripBaseUrl(res.data.next));
      setPrevUrl(stripBaseUrl(res.data.previous));
      setProposals(mapProposals(res.data.results));
    } catch (err) {
      console.error("Failed to fetch proposals:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const goToNext = () => { if (nextUrl) fetchProposals(nextUrl); };
  const goToPrev = () => { if (prevUrl) fetchProposals(prevUrl); };

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  return { proposals, loading, setProposals, refetch: fetchProposals, totalCount, nextUrl, prevUrl, goToNext, goToPrev };
}